import {
    type PointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from "react";
import { createAtom } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { resolveColumnMeta } from "../../columns/columnKinds";
import type { GridController } from "../../focus/gridController";
import type { DataGridInstance } from "../../types";
import { useConstant } from "@hooks/useConstant";
import {
    type ColumnIndicatorState,
    dropColumn,
    getColumnIndicatorAt,
    getSiblingColumnIds,
} from "./columnDrag";
import type { ColumnDragContextValue } from "./dragContext";
import type {
    DraggableApi,
    DragKindController,
    DragPreviewInfo,
} from "./dragKind";

interface ColumnDragSession {
    columnId: string;
    label: string;
    columnIds: string[];
    indicator: ColumnIndicatorState | null;
    cleanup: () => void;
}

/**
 * Header drag: pointer only (keyboard users reorder in ColumnSettings). The
 * drop lands between siblings in the same pin zone and group, through
 * `moveColumn`, so `columnState` adapters persist it like any reorder.
 */
export const useColumnDragController = <TData extends RowData>({
    instance,
    controller,
    announce,
}: {
    instance: DataGridInstance<TData>;
    controller: GridController;
    announce: (message: string) => void;
}): DragKindController<ColumnIndicatorState> & {
    contextValue: ColumnDragContextValue;
} => {
    const { table } = instance;
    const sourceRef = useRef<DraggableApi | null>(null);
    const pendingColumnIdRef = useRef<string | null>(null);
    const sessionRef = useRef<ColumnDragSession | null>(null);
    const indicator = useConstant(() =>
        createAtom<ColumnIndicatorState | null>(null),
    );
    // A table that unmounts mid-drag drops its listeners.
    useEffect(() => () => sessionRef.current?.cleanup(), []);

    const startColumnDrag = useCallback(
        (event: PointerEvent<HTMLElement>, columnId: string) => {
            const source = sourceRef.current;
            const isOnControl =
                event.target instanceof Element &&
                event.target.closest("[data-dt-no-drag]") !== null;
            if (!source || isOnControl) {
                return;
            }
            pendingColumnIdRef.current = columnId;
            source.setNodeRef(event.currentTarget);
            source.setActivatorNodeRef(event.currentTarget);
            source.listeners?.onPointerDown?.(event);
        },
        [],
    );

    const finish = useCallback(() => {
        const session = sessionRef.current;
        session?.cleanup();
        sessionRef.current = null;
        pendingColumnIdRef.current = null;
        indicator.set(null);
        controller.dragSource.set(null);
        return session;
    }, [controller, indicator]);

    const onStart = useCallback(
        (activatorEvent: Event | null) => {
            const columnId = pendingColumnIdRef.current;
            const column =
                columnId === null ? undefined : table.getColumn(columnId);
            const { viewport } = controller;
            if (columnId === null || !column || !viewport) {
                return false;
            }
            const session: ColumnDragSession = {
                columnId,
                label: resolveColumnMeta(column.columnDef, columnId)
                    .headerLabel,
                columnIds: getSiblingColumnIds(table, columnId),
                indicator: null,
                cleanup: () => undefined,
            };
            let clientX =
                activatorEvent instanceof globalThis.PointerEvent
                    ? activatorEvent.clientX
                    : 0;
            const update = () => {
                session.indicator = getColumnIndicatorAt({
                    viewport,
                    columnIds: session.columnIds,
                    sourceId: columnId,
                    clientX,
                });
                indicator.set(session.indicator);
            };
            const handleMove = (event: globalThis.PointerEvent) => {
                clientX = event.clientX;
                update();
            };
            document.addEventListener("pointermove", handleMove);
            viewport.addEventListener("scroll", update, { passive: true });
            session.cleanup = () => {
                document.removeEventListener("pointermove", handleMove);
                viewport.removeEventListener("scroll", update);
            };
            sessionRef.current = session;
            controller.dragSource.set({ kind: "column", id: columnId });
            announce(`Picked up ${session.label} column.`);
            return {
                label: session.label,
                input: "pointer",
            } satisfies DragPreviewInfo;
        },
        [announce, controller, indicator, table],
    );

    const onEnd = useCallback(() => {
        const session = finish();
        const target = session?.indicator?.target;
        if (!session || !target) {
            return;
        }
        dropColumn({
            table,
            sourceId: session.columnId,
            target,
            columnIds: session.columnIds,
        });
        announce(`Moved ${session.label} column.`);
    }, [announce, finish, table]);

    const contextValue = useMemo(
        (): ColumnDragContextValue => ({ startColumnDrag }),
        [startColumnDrag],
    );

    return {
        sourceRef,
        indicator,
        contextValue,
        onStart,
        onEnd,
        onCancel: finish,
    };
};
